import { useState } from 'react';
import FlagPriority from '../../../assets/icons/flag-priority.svg?react';
import FlagPriorityWhite from '../../../assets/icons/FlagPriorityWhite.svg?react';
import colorTokens from '../../../constants/design_system/colors';
import { flagWidth, PriorityFlagPropsMap, PriorityOptions } from '../../TaskTable/helpers/helper';
import BaseDropdown from './BaseDropdown';
import NoDragWrapper from './NoDragWrapper';

export type PriorityDropdownProps = {
  priority: PriorityOptions;
  size: 'small' | 'medium' | 'large';
  onSelect?: (priority: PriorityOptions) => void;
  onBlur?: () => void;
  hideOutline?: boolean;
  defaultOpen?: boolean;
  className?: string;
  position?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'topCenter' | 'topLeft' | 'topRight';
};

const PriorityDropdown = (props: PriorityDropdownProps) => {
  const [isDropdownOpen, setDropdownOpen] = useState(props.defaultOpen || false);

  const onSelect = (value: PriorityOptions) => {
    if (props.onSelect) {
      props.onSelect(value);
    }
    isDropdownOpen && setDropdownOpen(false);
  };

  return (
    <NoDragWrapper className="h-full">
      <BaseDropdown
        disableTransition
        trigger={['click']}
        open={isDropdownOpen}
        placement={props.position || 'bottomLeft'}
        onOpenChange={(open) => {
          setDropdownOpen(open);
          if (!open && props.onBlur) {
            props.onBlur();
          }
        }}
        dropdownRender={(menu) => (
          <div className="flex flex-col items-start justify-start bg-brand-white shadow-md rounded-lg p-2">
            {Object.values(PriorityOptions)
              .reverse()
              .map((priority) => (
                <PriorityLabel key={priority} priority={priority} size="large" onClick={() => onSelect(priority)} />
              ))}
          </div>
        )}
      >
        <div
          className={`${props.className ?? 'h-full'} flex items-center cursor-pointer`}
          style={{ outline: isDropdownOpen && !props.hideOutline ? `2px solid ${colorTokens.green[300]}` : 'none' }}
        >
          <PriorityLabel priority={props.priority} size={props.size} />
        </div>
      </BaseDropdown>
    </NoDragWrapper>
  );
};

export const PriorityLabel = (props: {
  priority: PriorityOptions;
  size: 'small' | 'medium' | 'large';
  onClick?: () => void;
}) => {
  const isKnown = !!PriorityFlagPropsMap[props.priority];
  if (!isKnown && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      `[PriorityLabel] unknown priority "${String(props.priority)}", falling back to NoPriority. ` +
        `Likely a backend/AI value that drifted from the enum.`
    );
  }
  const priority = isKnown ? props.priority : PriorityOptions.NoPriority;
  const { colorFill, label } = PriorityFlagPropsMap[priority];
  return (
    <div
      className={`${props.size === 'large' ? 'w-[180px] py-1' : props.size === 'medium' ? 'w-[110px]' : ''} h-full px-1 flex items-center gap-2 cursor-pointer hover:bg-neutral-200`}
      onClick={props.onClick}
    >
      <div className="flex items-center gap-1">
        {priority === PriorityOptions.NoPriority ? (
          <FlagPriorityWhite style={{ width: flagWidth }} />
        ) : (
          <FlagPriority style={{ width: flagWidth }} fill={colorFill} />
        )}
        {props.size !== 'small' && (
          <div className="text-sm font-normal" style={{ color: colorFill }}>
            {label}
          </div>
        )}
      </div>
    </div>
  );
};

export default PriorityDropdown;
